import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Collapse,
	Drawer,
	IconButton,
	List,
	ListItem,
	ListItemText,
	Paper,
	Toolbar,
	Tooltip,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import PushPinIcon from '@mui/icons-material/PushPin';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AnchorEntry, PopulatedEntry, PropertyDefinition, Repository } from '../types';
import { RepositoryEntryFieldsForm } from '../components/RepositoryEntryFieldsForm';
import type { UseRepositoryEntryEditFlowResult } from '../hooks/useRepositoryEntryEditFlow';
import type { UseRepositoryEntryDrawerChangesResult } from '../hooks/useRepositoryEntryDrawerChanges';
import UnsavedChangesDialog from '../components/UnsavedChangesDialog';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import useAuth from '../components/Auth';
import {
	entriesDeleteMany,
	repositoryEntryArchiveLocalAction,
	repositoryEntryUnarchiveLocalAction,
} from './contentSlice';
import { useRepositoryEntryArchiveMutation, useRepositoryEntryUnarchiveMutation } from '../services/repositoryEntries';
import { useEntryDeleteMutation } from '../services/records';
import { extractEntryTextFieldUrls } from '../common/util';
import { EntryExternalLinkActions } from '../components/EntryExternalLinkActions';

export type RepositoryEntryDrawerProps = {
	open: boolean;
	onClose: () => void;
	repositoryEntryId: string | null;
	editFlow: UseRepositoryEntryEditFlowResult;
	drawerChanges: UseRepositoryEntryDrawerChangesResult;
	populatedEntry: PopulatedEntry | null;
	repository: Repository | null;
	propertyDefinitions: PropertyDefinition[];
	isLoading: boolean;
	/** All anchors currently attached to this reference — an entry can host more than one. */
	attachedAnchors?: AnchorEntry[];
	anchorTargetRepositoryTitleByAnchorId?: Map<string, string | null>;
	onEditAnchor?: (anchor: AnchorEntry) => void;
	onUnpinAnchor?: (anchor: AnchorEntry) => void;
	onRemoveFromTimeline?: () => void;
};

export function RepositoryEntryDrawer({
	open,
	onClose,
	repositoryEntryId,
	editFlow,
	drawerChanges,
	populatedEntry,
	repository,
	propertyDefinitions,
	attachedAnchors,
	anchorTargetRepositoryTitleByAnchorId,
	onEditAnchor,
	onUnpinAnchor,
	onRemoveFromTimeline,
}: RepositoryEntryDrawerProps) {
	const { isAuthenticated } = useAuth();
	const dispatch = useDispatch();
	const [repositoryEntryArchiveMutation] = useRepositoryEntryArchiveMutation();
	const [repositoryEntryUnarchiveMutation] = useRepositoryEntryUnarchiveMutation();
	const [entryDeleteMutation] = useEntryDeleteMutation();
	const [isMoreOptionsOpen, setMoreOptionsOpen] = useState(false);
	const navigate = useNavigate();
	const { pendingChanges, discardPendingChanges, requestEntryChange } = drawerChanges;

	const fullPageHref = repositoryEntryId ? `/entries/${repositoryEntryId}` : '';
	const externalUrls = useMemo(
		() => extractEntryTextFieldUrls(propertyDefinitions, populatedEntry?.data),
		[propertyDefinitions, populatedEntry?.data],
	);

	// Covers drawer changes that bypass `requestEntryChange`, e.g. the entry being deleted.
	useEffect(() => {
		if (pendingChanges && pendingChanges.entryId !== repositoryEntryId) {
			discardPendingChanges();
		}
	}, [pendingChanges, repositoryEntryId, discardPendingChanges]);

	const handleFormSubmit = useCallback((event: React.FormEvent) => {
		event.preventDefault();
		drawerChanges.savePendingChanges();
		onClose();
	}, [drawerChanges, onClose]);

	const handleOpenInFullClick = useCallback((event: React.MouseEvent) => {
		event.preventDefault();
		requestEntryChange(null, () => {
			onClose();
			navigate(fullPageHref);
		});
	}, [requestEntryChange, onClose, navigate, fullPageHref]);

	const handleArchiveEntryClick = useCallback(() => {
		if (!populatedEntry || !repository?.id) {
			return;
		}
		if (isAuthenticated) {
			repositoryEntryArchiveMutation({
				entryId: populatedEntry.id as string,
				repositoryId: repository.id,
			});
		} else {
			dispatch(repositoryEntryArchiveLocalAction(populatedEntry.id as string));
		}
	}, [populatedEntry, repository?.id, isAuthenticated, dispatch, repositoryEntryArchiveMutation]);

	const handleDeleteAnchorClick = useCallback((anchor: AnchorEntry) => {
		if (isAuthenticated) {
			entryDeleteMutation({ id: anchor.id });
		} else {
			dispatch(entriesDeleteMany([anchor.id]));
		}
	}, [isAuthenticated, entryDeleteMutation, dispatch]);

	const handleUnarchiveEntryClick = useCallback(() => {
		if (!populatedEntry || !repository?.id) {
			return;
		}
		if (isAuthenticated) {
			repositoryEntryUnarchiveMutation({
				entryId: populatedEntry.id as string,
				repositoryId: repository.id,
			});
		} else {
			dispatch(repositoryEntryUnarchiveLocalAction(populatedEntry.id as string));
		}
	}, [populatedEntry, repository?.id, isAuthenticated, dispatch, repositoryEntryUnarchiveMutation]);

	return (
		<>
			<Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 420, xl: 600, /*height: 'calc(100dvh - 32px)', top: 16 */}, borderRadius: 0, maxWidth: '100%' } }} variant='persistent'>
				<Toolbar sx={{ gap: 1, borderBottom: 1, borderColor: 'divider', minHeight: 56 }}>
					<Box flex={1} minWidth={0}>
						<Typography variant="subtitle1" noWrap>
							{populatedEntry?.label}
						</Typography>
						{repository?.singular && (
							<Typography variant="caption" color="text.secondary" noWrap display="block">
								{repository.singular}
							</Typography>
						)}
					</Box>
					{repositoryEntryId && (
						<Tooltip title="Open in full" disableInteractive>
							<IconButton component={Link} to={fullPageHref} onClick={handleOpenInFullClick}>
								<ArrowForwardIcon />
							</IconButton>
						</Tooltip>
					)}
				</Toolbar>
				<Box sx={{ flex: 1 }} display="flex" flexDirection="column" minHeight={0}>
					{!populatedEntry ? (
						<Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
							<CircularProgress />
						</Box>
					) : (
						<Box component="form" onSubmit={handleFormSubmit} display="flex" flexDirection="column" flex={1} minHeight={0}>
							<Box sx={{ p: 2, overflow: 'auto', flex: 1 }} minHeight={0}>
								<RepositoryEntryFieldsForm
									key={populatedEntry.id}
									populatedEntry={populatedEntry}
									propertyDefinitions={propertyDefinitions}
									onChange={drawerChanges.addPendingValues}
									getRelationValues={drawerChanges.getRelationValues}
									onRelationChange={drawerChanges.setPendingRelationValues}
									onCreateRelatedEntryClick={editFlow.onAddRelatedEntryClick}
								/>
								{externalUrls.length > 0 && (
									<Box display="flex" flexWrap="wrap" gap={1} mt={2}>
										<EntryExternalLinkActions urls={externalUrls} />
									</Box>
								)}
							</Box>
							<Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }} display="flex" justifyContent="flex-end" alignItems='end' flexDirection="column" gap={1}>
								<Box display="flex" justifyContent="flex-end" alignItems="center" gap={1} width="100%">
									<Button onClick={onClose} sx={{ mr: 'auto' }}>
										Close
									</Button>
									<IconButton
										aria-label="More options"
										sx={{ color: 'text.secondary' }}
										onClick={() => setMoreOptionsOpen(!isMoreOptionsOpen)}
									>
										{isMoreOptionsOpen ? <ExpandLessIcon/> : <ExpandMoreIcon />}
									</IconButton>
									<Button
										type="submit"
										variant="contained"
										disabled={!pendingChanges || editFlow.anyBlockingDialog}
									>
										Save
									</Button>
								</Box>
								<Collapse in={isMoreOptionsOpen} sx={{ width: '100%' }}>
									<Box display="flex" flexDirection="column" gap={1} width="100%">
										{attachedAnchors?.map((anchor) => (
											<List key={`attached-anchor-${anchor.id}`} disablePadding sx={{ width: '100%' }}>
												<ListItem
													component={Paper}
													elevation={0}
													sx={{ border: 1, borderColor: 'divider', gap: 1, width: '100%' }}
													secondaryAction={
														<Box display="flex" alignItems="center" gap={1}>
															<IconButton size="small" aria-label="Edit anchor" onClick={() => onEditAnchor?.(anchor)} disabled={editFlow.anyBlockingDialog}>
																<EditIcon fontSize="small" />
															</IconButton>
															<IconButton size="small" aria-label="Unpin from reference" onClick={() => onUnpinAnchor?.(anchor)} disabled={editFlow.anyBlockingDialog}>
																<PushPinIcon fontSize="small" />
															</IconButton>
															<IconButton size="small" aria-label="Remove anchor" onClick={() => handleDeleteAnchorClick(anchor)} disabled={editFlow.anyBlockingDialog}>
																<CloseIcon fontSize="small" />
															</IconButton>
														</Box>
													}
												>
													<ArchitectureIcon sx={{color: 'text.secondary'}} />
													<ListItemText
														primary="Anchor"
														secondary={anchorTargetRepositoryTitleByAnchorId?.get(anchor.id) ?? undefined}
													/>
												</ListItem>
											</List>
										))}
										
										{onRemoveFromTimeline && (
											<Button
												aria-label="Remove from timeline"
												size="small"
												sx={{ alignSelf: 'end', color: 'text.secondary' }}
												startIcon={<EventBusyIcon fontSize="small" />}
												onClick={onRemoveFromTimeline}
												disabled={editFlow.anyBlockingDialog}
											>
												Remove from timeline
											</Button>
										)}
										<Button
											aria-label={populatedEntry.isArchived ? 'Unarchive entry' : 'Archive entry'}
											size="small"
											sx={{ alignSelf: 'end', color: 'text.secondary' }}
											startIcon={populatedEntry.isArchived ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
											onClick={populatedEntry.isArchived ? handleUnarchiveEntryClick : handleArchiveEntryClick}
											disabled={editFlow.anyBlockingDialog}
										>
											{populatedEntry.isArchived ? 'Unarchive' : 'Archive'}
										</Button>
									</Box>
								</Collapse>
							</Box>
						</Box>
					)}
				</Box>
			</Drawer>
			<UnsavedChangesDialog {...drawerChanges.unsavedChangesDialogProps} />
		</>
	);
}
